import { onMounted } from 'vue'
import { useQuoteDetail } from './useQuoteDetail.js'
import { useGlobalCall } from '../calls/useGlobalCall.js'
import { useQuoteComments } from './useQuoteComments.js'
import { useQuoteHistory } from './useQuoteHistory.js'
import { useQuotePDF } from './useQuotePDF.js'
import { useQuoteDetailFormations } from './useQuoteDetailFormations.js'
import { useQuoteDetailEditor } from './useQuoteDetailEditor.js'
import { useQuoteDetailPageState } from './useQuoteDetailPageState.js'
import { useQuoteDetailPageActions } from './useQuoteDetailPageActions.js'
import { useQuoteDetailPageWatchers } from './useQuoteDetailPageWatchers.js'
import { useQuoteDetailPageInit } from './useQuoteDetailPageInit.js'
import { useDuplicateDetection } from './useDuplicateDetection.js'
import { useUnpaidDetection } from './useUnpaidDetection.js'

/**
 * Composable principal pour orchestrer toute la logique de la page QuoteDetail
 * Limite: 150 lignes max
 */
export function useQuoteDetailPage(quoteId) {
  const quoteDetailData = useQuoteDetail(quoteId)
  const { quote } = quoteDetailData
  const { isQuoteSigned } = useQuoteDetailPageState(quote)
  const commentsData = useQuoteComments(quoteId)
  const historyData = useQuoteHistory(quoteId)
  const pdfData = useQuotePDF(quoteId, quote, { 
    updateStatus: quoteDetailData.updateStatus, 
    loadQuote: quoteDetailData.loadQuote, 
    loadHistory: historyData.loadHistory 
  })
  const editorData = useQuoteDetailEditor(quoteId, quote, { 
    generatePDFPreview: pdfData.generatePDFPreview, 
    showAmountModal: pdfData.showAmountModal, 
    quoteAmountInput: pdfData.quoteAmountInput, 
    isFromEditor: pdfData.isFromEditor 
  })
  
  // Gérer l'événement saved de l'éditeur pour mettre à jour le quote
  const handleQuoteEditorSaved = (response) => {
    if (response && response.quoteAmount !== undefined && quote.value) {
      quote.value.quote_amount = response.quoteAmount
    }
  }
  const globalCallData = useGlobalCall()
  const duplicateData = useDuplicateDetection(quoteId)
  const unpaidData = useUnpaidDetection(quoteId)
  const formationsData = useQuoteDetailFormations({
    quote,
    isEditing: quoteDetailData.isEditing,
    editedQuote: quoteDetailData.editedQuote,
    getValue: quoteDetailData.getValue
  })
  const pageActionsData = useQuoteDetailPageActions(quoteId, quote, {
    deleteQuote: quoteDetailData.deleteQuote,
    updateStatus: quoteDetailData.updateStatus,
    loadQuote: quoteDetailData.loadQuote,
    loadHistory: historyData.loadHistory,
    loadComments: commentsData.loadComments,
    startGlobalCall: globalCallData.startCall,
    generateAndSendQuote: pdfData.generateAndSendQuote,
    generatePDFPreview: pdfData.generatePDFPreview
  })
  const datesHandlers = pageActionsData.createDatesHandlers(quoteDetailData.isEditing, quoteDetailData.editedQuote)

  useQuoteDetailPageWatchers(quoteDetailData.editedQuote, quoteDetailData.isEditing, formationsData.selectedSession)

  const allData = {
    ...quoteDetailData,
    isQuoteSigned,
    ...commentsData,
    ...historyData,
    ...duplicateData,
    ...unpaidData,
    ...pdfData,
    ...editorData,
    callInProgress: globalCallData.callInProgress,
    currentCallQuoteId: globalCallData.quoteId,
    sendingEmail: globalCallData.sendingEmail,
    showNoAnswerModal: globalCallData.showNoAnswerModal,
    sendEmailOnNoAnswer: globalCallData.sendEmailOnNoAnswer,
    startCall: pageActionsData.startCall,
    markCallAsNoAnswer: globalCallData.markCallAsNoAnswer,
    confirmNoAnswer: globalCallData.confirmNoAnswer,
    ...formationsData,
    handleDatesDisponibilitesChange: datesHandlers.handleDatesDisponibilitesChange,
    handleDatesPeriodesChange: datesHandlers.handleDatesPeriodesChange,
    handleDatesInconnuesChange: datesHandlers.handleDatesInconnuesChange,
    viewQuotePDF: pageActionsData.viewQuotePDF,
    handleDeleteQuote: pageActionsData.handleDeleteQuote,
    restoreFromTrash: pageActionsData.restoreFromTrash,
    handleQuoteEditorSaved
  }

  const { initialize } = useQuoteDetailPageInit(
    formationsData.loadFormationsData,
    quoteDetailData.loadCommercials,
    quoteDetailData.loadQuote,
    commentsData.loadComments,
    historyData.loadHistory,
    duplicateData.loadDuplicates
  )

  onMounted(initialize)
  onMounted(unpaidData.loadUnpaid)

  return allData
}
